import { useState, useEffect, useCallback, useRef } from "react";
import { api } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Activity, Wifi, WifiOff, AlertTriangle, Play, Square } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, ReferenceLine, Tooltip, CartesianGrid } from "recharts";


export default function LiveMeasurementPage() {
  const [seriesList, setSeriesList] = useState([]);
  const [selectedSeries, setSelectedSeries] = useState("");
  const [threshold, setThreshold] = useState(2.5);
  const [measurements, setMeasurements] = useState([]);
  const [isPolling, setIsPolling] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const intervalRef = useRef(null);

  useEffect(() => {
    api.get(`/api/series`)
      .then(({ data }) => setSeriesList(data.series || []))
      .catch(() => {});
  }, []);

  const fetchMeasurements = useCallback(async () => {
    if (!selectedSeries) return;
    try {
      const { data } = await api.get(`/api/measurements`, {
        params: { series: selectedSeries, limit: 100 },
      });
      const sorted = (data.measurements || []).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      setMeasurements(sorted);

      const newAlerts = sorted.filter((m) => m.voltage < threshold).slice(-5);
      if (newAlerts.length > 0) setAlerts(newAlerts);
    } catch {}
  }, [selectedSeries, threshold]);

  const startPolling = () => {
    if (!selectedSeries) { toast.error("Selectionnez une serie"); return; }
    setIsPolling(true);
    fetchMeasurements();
    intervalRef.current = setInterval(fetchMeasurements, 2000);
    toast.success("Mesure en direct activee");
  };

  const stopPolling = () => {
    setIsPolling(false);
    if (intervalRef.current) clearInterval(intervalRef.current);
    toast.info("Mesure en direct arretee");
  };

  useEffect(() => {
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, []);

  const chartData = measurements.map((m) => ({
    time: new Date(m.timestamp).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", second: "2-digit" }),
    voltage: m.voltage,
    cell_id: m.cell_id,
  }));

  return (
    <div className="space-y-6" data-testid="live-measurement-page">
      {/* Controls */}
      <Card className="bg-cyber-surface border-neon-blue/10 rounded-xl">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-base text-white flex items-center gap-2">
            <Activity className="w-4 h-4 text-neon-cyan" />
            Configuration de la mesure
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 items-end">
            <div className="w-48">
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-1.5 block">Serie</Label>
              <Select value={selectedSeries} onValueChange={setSelectedSeries}>
                <SelectTrigger className="bg-cyber-bg/50 border-neon-blue/20 font-mono text-sm h-9 rounded-lg" data-testid="live-series-select">
                  <SelectValue placeholder="Selectionnez" />
                </SelectTrigger>
                <SelectContent>
                  {seriesList.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="w-40">
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-1.5 block">Seuil (V)</Label>
              <Input
                type="number"
                step="0.1"
                value={threshold}
                onChange={(e) => setThreshold(parseFloat(e.target.value) || 0)}
                className="bg-cyber-bg/50 border-neon-blue/20 font-mono text-sm h-9 rounded-lg"
                data-testid="live-threshold-input"
              />
            </div>
            <div className="flex gap-2">
              {!isPolling ? (
                <Button onClick={startPolling} className="bg-gradient-to-r from-neon-blue to-neon-cyan text-white font-bold uppercase tracking-wide rounded-lg h-9 neon-glow-blue" data-testid="live-start-button">
                  <Play className="w-4 h-4 mr-2" />
                  Demarrer
                </Button>
              ) : (
                <Button onClick={stopPolling} variant="outline" className="border-neon-red text-neon-red hover:bg-neon-red-muted rounded-lg h-9 bg-transparent neon-glow-red" data-testid="live-stop-button">
                  <Square className="w-4 h-4 mr-2" />
                  Arreter
                </Button>
              )}
            </div>
            <Badge
              variant="outline"
              className={`font-mono text-[10px] uppercase tracking-wider h-7 flex items-center gap-1.5 ${
                isPolling
                  ? "border-neon-cyan/30 text-neon-cyan bg-neon-cyan-muted neon-glow-cyan"
                  : "border-neon-blue/10 text-muted-foreground"
              }`}
              data-testid="live-connection-status"
            >
              {isPolling ? <Wifi className="w-3 h-3 animate-pulse-neon" /> : <WifiOff className="w-3 h-3" />}
              {isPolling ? "Connecte" : "Deconnecte"}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Chart */}
      <Card className="bg-cyber-surface border-neon-blue/10 rounded-xl" data-testid="live-chart-card">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-base text-white">Tension en temps reel</CardTitle>
        </CardHeader>
        <CardContent>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(59, 130, 246, 0.08)" />
                <XAxis
                  dataKey="time"
                  tick={{ fill: "#4B5E7A", fontSize: 10, fontFamily: "JetBrains Mono" }}
                  axisLine={{ stroke: "rgba(59, 130, 246, 0.15)" }}
                  tickLine={false}
                />
                <YAxis
                  domain={["auto", "auto"]}
                  tick={{ fill: "#4B5E7A", fontSize: 10, fontFamily: "JetBrains Mono" }}
                  axisLine={{ stroke: "rgba(59, 130, 246, 0.15)" }}
                  tickLine={false}
                  label={{ value: "V", position: "insideTopLeft", fill: "#4B5E7A", fontFamily: "JetBrains Mono", fontSize: 11 }}
                />
                <Tooltip
                  contentStyle={{ backgroundColor: "#111d32", border: "1px solid rgba(59, 130, 246, 0.2)", borderRadius: "8px", fontFamily: "JetBrains Mono", fontSize: "11px" }}
                  labelStyle={{ color: "#6B7994" }}
                />
                <ReferenceLine y={threshold} stroke="#EF4444" strokeDasharray="5 5" label={{ value: `Seuil: ${threshold}V`, fill: "#EF4444", fontSize: 10, fontFamily: "JetBrains Mono" }} />
                <Line type="monotone" dataKey="voltage" stroke="#3B82F6" strokeWidth={2} dot={false} activeDot={{ r: 4, fill: "#3B82F6", stroke: "rgba(59, 130, 246, 0.4)", strokeWidth: 6 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex flex-col items-center justify-center h-[350px] text-muted-foreground">
              <Activity className="w-12 h-12 mb-4 text-neon-blue/20" />
              <p className="font-mono text-sm">En attente de donnees...</p>
              <p className="font-mono text-xs mt-1">Selectionnez une serie et demarrez la mesure</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Alerts */}
      {alerts.length > 0 && (
        <Card className="bg-cyber-surface border-neon-red/30 rounded-xl neon-glow-red" data-testid="live-alerts-card">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-base text-neon-red flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Alertes de seuil
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {alerts.map((a, i) => (
                <div key={i} className="flex items-center gap-3 p-2 bg-neon-red-muted border border-neon-red/20 rounded-lg">
                  <AlertTriangle className="w-4 h-4 text-neon-red shrink-0" />
                  <span className="font-mono text-xs text-neon-red">
                    Cellule {a.cell_id?.slice(0, 8)} — {a.voltage}V (sous le seuil de {threshold}V)
                  </span>
                  <span className="font-mono text-[10px] text-muted-foreground ml-auto">
                    {new Date(a.timestamp).toLocaleTimeString("fr-FR")}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ESP32 Info */}
      <Card className="bg-cyber-surface border-neon-cyan/20 rounded-xl border-dashed" data-testid="esp32-info-card">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-neon-cyan-muted border border-neon-cyan/20 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
              <Wifi className="w-4 h-4 text-neon-cyan" />
            </div>
            <div>
              <p className="text-sm font-medium text-white mb-1">Integration ESP32 (Phase 2)</p>
              <p className="text-xs text-muted-foreground font-mono leading-relaxed">
                L'ESP32 pourra envoyer des mesures via POST /api/measurements avec le format JSON :
                <br />
                {`{ "cell_id": "ID_CELLULE", "voltage": 3.75, "timestamp": "2025-01-01T12:00:00Z" }`}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
